/**
 * Figure S7's "📨 Shared into this portfolio" card.
 *
 * What other organisations have consented to share IN with this portfolio's
 * entities, each at the rung they chose. These are never positions on a map
 * and never matched against anything — the standing line above the card says
 * why, and this card repeats the same rule in its own words.
 */
import type { PortfolioEntitlementSharedInSource } from '../../types/whitespace'

export interface SharedIntoPortfolioProps {
  sources: PortfolioEntitlementSharedInSource[]
}

/** The rungs, in disclosure/filters.py's own order (1 … 6). */
export const RUNG_LABEL: Record<number, string> = {
  1: 'Anonymous Match',
  2: 'Categorical Profile',
  3: 'Pattern Insights',
  4: 'Summary',
  5: 'Detailed Analysis',
  6: 'Full Collaboration',
}

/** "Shared in · Northwind Labs · Categorical Profile" */
export function sharedInChipLabel(source: PortfolioEntitlementSharedInSource): string {
  const who = source.org_name?.trim() || 'Anonymous organisation'
  const rung = RUNG_LABEL[source.disclosure_level] ?? `rung ${source.disclosure_level}`
  return `Shared in · ${who} · ${rung}`
}

export default function SharedIntoPortfolio({ sources }: SharedIntoPortfolioProps) {
  return (
    <div data-part="SharedIntoPortfolio" className="light-card p-4">
      <h3 className="text-[15px] font-bold text-iflow-900">📨 Shared into this portfolio</h3>
      <p className="mt-1 text-[12px] text-gray-500">At their rung — never as positions.</p>

      {sources.length === 0 ? (
        <p className="mt-2 text-[12.5px] text-gray-600">
          No organisation has shared anything in with your entities yet.
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {sources.map((source) => (
            <span
              key={source.source_project_id}
              data-testid="shared-in-chip"
              className="rounded-lg bg-violet-50 px-2.5 py-1 text-[11.5px] font-semibold text-violet-800"
            >
              {sharedInChipLabel(source)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
